import React from "react";
import {objToArrKH, tailCheck} from "../tools/CommonReact.tsx";
import {FormHint} from "./CommonUIPopups.jsx";
import {currencySymbols, getBizDur, severitiesDefs, statusPillArr} from "./CommonUIData.jsx";
import {abvNum, commafyPlural, funcOrValOr, minUnitsBiz, objRmKeysC, pickKeys} from "../tools/CommonJS.ts";
import {DispAgoAndTipDateTime, Title, TitleAbvNum} from "./CommonUIComponenets.jsx";
import {appCtx, genTooltip} from "./AppContext.tsx";
import {Button} from "../tools/Inputs.tsx";
import {TextEllip} from "../tools/UIBlocks.jsx";
import {ReadMorePopUpWrapper} from "../tools/UIPopMenu.jsx";
import {ROUTES} from "../routePaths.js";

export const DispBizDurAndTipHours = ({m}) =>
    <TitleAbvNum val={getBizDur(m)} tipFmt={m <= 60 * 8 ? undefined : commafyPlural((m / 60 + 0.5 | 0), "business hour")}/>;

export function SavingsSummary({title, time, fteRsp, isPast = false, tip = "", className = ""}) {
    return !!fteRsp?.FTE && <div className={tailCheck(className, "bg-[#666AFF]/5", "rounded", "p- px-3 py-2", "flex flex-col", "gap-1", "-mx-3")}>
        <div className="flex items-baseline gap-2">
            {title}:
            <FormHint
                tip={`${tip ? `${tip} ` : ""}The sum is based on an FTE salary of ${currencySymbols[fteRsp.currency]?.s || ""}${fteRsp.FTE.toLocaleString()} for a full-time cybersecurity professional.`}/>
        </div>
        <div className="font-bold" data-test={`${title.toLowerCase().toKebabCase()}-saving-summary`}>
            <DispBizDurAndTipHours m={time}/> (
            <span className={isPast ? "text-[#01EAC1]" : "text-[#666AFF]"}>
                {currencySymbols[fteRsp.currency]?.s}{abvNum((time / minUnitsBiz.year * fteRsp.FTE) | 0)}
            </span>
            &nbsp;FTE)
        </div>
    </div>;
}

export const ListItem = ({onClick = undefined, handleClr = undefined, noHandle = false, leftTip = undefined, active = undefined,
    className = "", children = undefined, ...props}) =>
    <div
        {...{onClick, ...props}}
        className={tailCheck(className,
                             "rounded-md flex items-center overflow-hidden", "gap-3", "pr-3", "h-14", "mb-[0.6rem]",
                             onClick && "cursor-pointer", active ? "bg-bg3" : "bg-bg1")}>
        {!noHandle &&
            <div
                data-test="list-item-border"
                className={`w-2 h-full rounded-l-md shrink-0 min-w-0 ${active ? "bg-prm-main" : severitiesDefs[handleClr]?.bg ?? handleClr ?? "bg-handle"}`}
                {...genTooltip(leftTip)}/>}
        {children}
    </div>;

export function StatusPill({h = undefined, val = undefined, className = undefined, classNameTitle = undefined,
    dataTest = undefined, noTip = false, statusPills = statusPillArr, ...props}) {
    const currDef = statusPills.find(pill => pill.h === h);
    const {tip, border, ...rest} = props;

    const tipValue = props?.tip ? typeof props.tip === "function" ? props.tip(val) : props.tip : currDef?.tip?.(val);
    const borderValue = props?.border || currDef?.border;
    className = funcOrValOr(className);

    return <Button
        {...(noTip || genTooltip(tipValue))}
        classNameDot="top-0.5 right-1.5"
        {...rest}
        className={tailCheck(className, "flex items-center border border-1", "mr-", "gap-1.5", "px-2 py-0.5",
                             "rounded-full", "font-thin", "text-2xs text-h2", borderValue)}
        data-test={dataTest || (typeof h === "string" && `status-pill-${h.toSnakeCase()}`)}>
        {val && <Title>{typeof val === "number" ? abvNum(val) : val}</Title>}
        <TextEllip className={classNameTitle}>{h ?? props.children}</TextEllip>
    </Button>;
}

export function StatusPillsAutoFit({rowData, pills = statusPillArr, severity = undefined, repo = undefined, rowExtra = undefined, onChange = undefined, align = "start"}) {
    const sev = funcOrValOr(severity, rowData) ?? rowData?.severity;
    const navBase = repo && {
        ...(repo.modules && {modules: repo.modules}),
        topicCode: repo.topicCode ?? rowData?.topicCode,
        ...pickKeys(rowData, ["device", "deviceId", "param", "paramExtra"])
    };
    return (
        <ReadMorePopUpWrapper autoFit className={align === "end" ? "justify-end" : "justify-start"}>
            {pills.filter(p => (p.getVal ?? (r => r[p.k]))(rowData, rowExtra)).map(pill => {
                const val = (pill.getVal ?? (r => r[pill.k]))(rowData, rowExtra);
                const willNav = pill.navParams && navBase;
                const onClick = (pill.onClick || willNav) ? e => {
                    e.stopPropagation();
                    pill.onClick?.(onChange);
                    willNav && appCtx.nav.to(ROUTES.ACTION_LOG, {...navBase, ...pill.navParams});
                } : undefined;
                return <StatusPill
                    key={pill.k} h={funcOrValOr(pill.h, val)} val={!pill.noVal && val} onClick={onClick}
                    tip={pill.tip && funcOrValOr(pill.tip, val)}
                    className={funcOrValOr(pill.className, sev)}
                    border={pill.border}/>;
            })}
        </ReadMorePopUpWrapper>
    );
}

export const formatDeviceInfo = (deviceInfo, clientVers = undefined) => {
    const clientVerChannel = clientVers?.find(cv => cv.clientVer === deviceInfo?.clientVersion && cv.clientType === deviceInfo?.clientType)?.channel;

    const defs = [
        {
            h: "Host Name",
            k: ["hostname", "isVDI", "isDecommissioned"],
            d: (val, deviceInfo) => val && <div className="flex items-center gap-3">{val}{deviceInfo?.isVDI &&
                <span className="icon-vdi leading-5 text-lg text-title"/>}</div>
        },
        {h: "IP Address", k: ["ip", "IP"]},
        {h: "Operating System", k: ["os", "OS"]},
        {h: "Group Membership", k: "groupMembership", d: (val) => val?.map(v => v.groupName)},
        {
            h: "Sensor Version", k: ["clientVersion", "clientType"],
            d: (val) => val && <div className="flex gap-2">
                {val}
                <p className={clientVerChannel === "latest" ? "text-prm-dark" : "text-pending"}>
                    {clientVerChannel ? `(${clientVerChannel})` : "(update available)"}
                </p>
            </div>
        },
        {h: "Last Report", k: "lastReport", d: (val) => <DispAgoAndTipDateTime tm={val}/>},
        {h: "Last Remediation", k: "lastRemediation", d: (val) => <DispAgoAndTipDateTime tm={val}/>},
        ...(!deviceInfo?.labels?.length ? [] : [{h: "Labels", k: "labels", d: (val) => val.join(", ")}]),
        {
            h: "Domain Name", d: (_, deviceInfo) => deviceInfo?.metadata?.DomainName && (deviceInfo.metadata.DomainName
                + (!deviceInfo?.hostname ? "" : ` (${deviceInfo.hostname}.${deviceInfo.metadata.DomainName})`))
        },
        {
            h: "Metadata", k: "metadata", d: (val) => val && objToArrKH(pickKeys(deviceInfo.metadata,
                                                                                 "HW_CPU", "HW_Model", "HW_RAM", "HW_Serial", "OsBuild", "OsDisplayVersion", "MachineType", "NodeVendor", "MacAddress"))
        }
    ];

    let dispItems = defs.map(info =>
        ({
            ...info, k: info.d ?
                info.d(deviceInfo?.[Array.isArray(info.k) ? info.k[0] : info.k], deviceInfo)
                : Array.isArray(info.k) ? info.k.map(key => deviceInfo?.[key]).filter(Boolean)[0]
                    : deviceInfo?.[info?.k]
        }));
    dispItems.splice(defs.length - 1, 0,
                     ...(!deviceInfo ? [] : objToArrKH(objRmKeysC(deviceInfo, defs.map(def => def.k).flat()))));  // add deviceInfo fields that weren't augmented

    return dispItems;
};
